import type { PopupStackController } from 'popups/PopupStackController';
import { v4 } from 'uuid';
import type { App } from '../app/App';
import { InventoryItem } from '../app/resources/types';
import { GAME_ECONOMY_SETTINGS } from '../game/GameConstants';
import { ShopPopup } from '../game/views/popups/ShopPopup';
import type { IAPPlacement, IAPProduct } from '../app/iap/types';
import { iapPlacementData, IAPProductId } from '../app/iap/types';
import { OfferLocation, OfferTrigger } from '../app/offers/offersTypes';
import { OfferInteractionLocation } from '../app/tracking/types';
import { UpsellPopup } from '../game/views/popups/UpsellPopup';
import { UpsellInventoryView } from '../mainMenu/shopPage/UpsellInventoryView';

function getCoinOfferForLegacyBundle(catalog: IAPProduct[], specialOffer: IAPProduct | null): IAPProduct | null {
  if (!specialOffer) return null;
  const coinPackId = {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    Band_7: IAPProductId.CoinPack06,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    Band_6: IAPProductId.CoinPack05,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    Band_5: IAPProductId.CoinPack05,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    Band_4: IAPProductId.CoinPack04,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    Band_3: IAPProductId.CoinPack03,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    Band_2: IAPProductId.CoinPack02,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    Band_1: IAPProductId.CoinPack01,
  }[specialOffer.variantId];

  return catalog.find((product) => product.id === (coinPackId as string)) ?? null;
}

async function openUpsellPopup(app: App, placement: IAPPlacement, impressionId: string): Promise<boolean | null> {
  const {
    iap,
    controller: { popups },
  } = app;

  if (!iap) return false;

  const catalog = await iap.getCatalogAsync(OfferLocation.Shop, OfferTrigger.NotEnoughCoins);

  if (!catalog) {
    return false;
  }

  const legacyUpsellOffer =
    catalog?.find((product) => product.id === (IAPProductId.LegacyUpsellOffer as string)) ?? null;
  const coinOffer = getCoinOfferForLegacyBundle(catalog, legacyUpsellOffer);

  if (!legacyUpsellOffer || !coinOffer) {
    return false;
  }

  const upsellInventoryView = new UpsellInventoryView({
    coinOffer,
    specialOffer: legacyUpsellOffer,
  });

  const { promise, resolve } = Promise.withResolvers<boolean | null>();

  const upsellPopup = new UpsellPopup({
    upsellInventoryView,
  });

  upsellPopup.onCloseButtonClicked.connect(async () => {
    await popups.closeActivePopup();
    resolve(false);
  });

  upsellPopup.onRequestFullShop.connect(async () => {
    await popups.closeActivePopup();
    resolve(null);
  });

  let purchaseSuccessful = false;

  upsellInventoryView.onItemClicked.connect(async (product) => {
    purchaseSuccessful = await iap.makePurchase({
      product,
      placement,
      showResults: true,
      offerImpressionId: impressionId,
    });
    if (!purchaseSuccessful) return;
    await popups.closeActivePopup();
    resolve(purchaseSuccessful);
  });

  void popups.addPopup({
    popupInstance: upsellPopup,
  });

  return promise;
}

export async function openShopPopup(app: App, placement: IAPPlacement, cantAffordPurchase = false): Promise<boolean> {
  const {
    stateController,
    iap,
    controller: { popups },
    tracking,
    resources,
  } = app;

  const { resolve, promise } = Promise.withResolvers<boolean>();

  // check if shop is already open
  if (popups.hasActivePopup(ShopPopup) || !iap) {
    resolve(false);
    return promise;
  }

  const impressionId = v4();

  if (cantAffordPurchase) {
    const upsellResult = await openUpsellPopup(app, placement, impressionId);
    if (upsellResult !== null) {
      resolve(upsellResult);
      return promise;
    }
  }

  const shopPopup = new ShopPopup(app, {
    onProductPurchased: iap.onPurchaseSuccessful,
    purchaseHistory: iap.getPurchaseHistoryAsync(),
    getShopCatalog: () => iap.getCatalogAsync(OfferLocation.Shop, OfferTrigger.OpenShop),
    state: stateController,
  });

  trackShopOpen({
    app,
    shopPopup,
    impressionId,
    placement,
  });

  shopPopup.onOffersVisible.connect((products) => {
    products.forEach((product) => {
      tracking.trackOfferShown({
        product,
        location: OfferInteractionLocation.ShopView,
        impressionId,
      });
    });
  });

  shopPopup.onCloseClicked.connect(() => {
    void closeShopPopup(popups, shopPopup);
    resolve(false);
  });

  shopPopup.onPurchaseItem.connect(async (product) => {
    let storeEntryPoint = shopPopup.isExpanded() ? 'fullShop' : 'curatedShop';
    if (app.controller.sceneManager.activeScene?.id === 'Game') {
      storeEntryPoint = shopPopup.isExpanded() ? 'levelFullShop' : 'levelCuratedShop';
    }

    const purchaseSuccessful = await iap.makePurchase({
      product,
      placement,
      showResults: true,
      offerImpressionId: impressionId,
      storeEntryPoint,
    });
    if (!purchaseSuccessful) return;
    shopPopup.onPurchaseItem.disconnectAll();

    await closeShopPopup(popups, shopPopup);
    resolve(true);
  });

  shopPopup.onFreeGiftClaimed.connect(() => {
    resources.addShopFreeGiftReward({ [InventoryItem.Coin]: GAME_ECONOMY_SETTINGS.freeShopGiftCoinReward });
    stateController.markFreeShopGiftAsOpened();
  });

  shopPopup.onFreeGiftRefreshed.connect(() => {
    stateController.checkFreeShopGiftForNewDay();
  });

  shopPopup.onFreeGiftShown.connect(() => {
    stateController.markFreeShopGiftAsShown();
  });

  await popups.addPopup({
    popupInstance: shopPopup,
    settings: {
      overlayFillOpacity: 0.9,
    },
  });

  return promise;
}

function trackShopOpen(config: {
  app: App;
  shopPopup: ShopPopup;
  placement: IAPPlacement;
  impressionId: string;
}): void {
  const { app, shopPopup, placement, impressionId } = config;

  const metaProperties = [
    {
      metaPropertyName: 'Impression Id',
      metaPropertyValue: impressionId,
    },
  ];

  shopPopup.onExpanded.connect(() => {
    app.tracking.trackUIInteraction({
      action: 'Click',
      contextName: iapPlacementData[placement].uiInteractionContext,
      elementId: 'fullShop',
      metaProperties,
    });
  });

  app.tracking.trackUIInteraction({
    action: 'Click',
    contextName: iapPlacementData[placement].uiInteractionContext,
    elementId: 'curatedShop',
    metaProperties,
  });
}

async function closeShopPopup(popups: PopupStackController, shopPopup: ShopPopup): Promise<void> {
  void shopPopup.hideTopBar();
  void popups.closeActivePopup();
  shopPopup.onPurchaseItem.disconnectAll();
}
